<template>
  <div class="content">
    <!-- Nagłówek sekcji -->
    <h3 class="center-aligned">{{ t('contacts').toUpperCase() }}:</h3>
    
    <!-- Formularz dodawania pojedynczego kontaktu -->
    <div class="contact-form center-aligned">
      <div class="form-group">
        <label for="contactType" class="label-contact-type">{{ t('contact-type') }}:</label>
        <!-- Wybór typu kontaktu (mail/telefon) -->
        <select id="contactType" v-model.number="contactType">
          <option :value="0">{{ t('email') }}:</option>
          <option :value="1">{{ t('phone-number') }}:</option>
        </select>
      </div>
      <div class="form-group">
        <label for="contactInfo" class="label-contact-info"> {{ t('contact') }}:</label>
        <!-- Pole na mail lub telefon -->
        <input id="contactInfo" v-model="contactInfo" type="text" />
      </div>
      <div class="form-group">
        <label for="description" class="label-description">{{ t('contact-description') }}:</label>
        <!-- Krótki opis -->
        <input id="description" v-model="description" type="text" maxlength="100" />
      </div>
      <!-- Dodaj kontakt, tylko jeśli oba pola wypełnione -->
      <button @click="addContact" :disabled="!contactInfo || !description" class="button-save"> {{ t('add') }}</button>

      <div>
        {{ t('or') }}
      </div>

      <!-- Sekcja dodawania kontaktów z pliku CSV -->
      <div class="file-upload center-aligned">
        <!-- Input do CSV, tylko pliki CSV -->
        <input type="file" @change="handleFileUpload" accept=".csv" ref="fileInput"/>
        <div>
          <!-- Pokazanie nazwy wgranego pliku i przycisk X do usunięcia -->
          <span v-if="uploadedFileName">{{ uploadedFileName }} <button @click="removeFile" class="button-delete">X</button></span>
        </div>
        <!-- Podgląd kontaktów przygotowanych do dodania z CSV -->
        <div v-if="csvContacts.length > 0">
          <h4> {{ t('contacts-from-csv-ready-to-add') }}:</h4>
          <table class="csv-contact-table center-aligned">
            <thead>
              <tr>
                <th>{{ t('ordinal-number') }}</th>
                <th>{{ t('contact-type') }}</th>
                <th>{{ t('contact') }}</th>
                <th>{{ t('description') }}</th>
                <th>{{ t('action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(contact, index) in csvContacts" :key="contact.id">
                <td>{{ index + 1 }}</td>
                <td>{{ contact.type }}</td>
                <td>{{ contact.info }}</td>
                <td>{{ contact.description }}</td>
                <td><button @click="removeCSVContact(index)" class="button-delete">{{ t('delete') }}</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Przycisk do masowego dodania kontaktów z CSV -->
      <button v-if="csvContacts.length > 0" @click="submitCsvContacts" class="button-save">{{ t('add-contacts-from-csv') }}</button>
    </div>

    <!-- Lista zapisanych kontaktów -->
    <div v-if="contacts.length > 0">
      <table class="contact-table center-aligned">
        <thead>
          <tr>
            <th>{{ t('ordinal-number') }}</th>
            <th>{{ t('contact-type') }}</th>
            <th>{{ t('contact') }}</th>
            <th>{{ t('description') }}</th>
            <th>{{ t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(contact, index) in paginatedContacts" :key="contact.id">
            <td>{{ index + 1 }}</td>
            <td>{{ contact.type === 0 ? t('email') : t('phone-number') }}</td>
            <td>{{ contact.info }}</td>
            <td>{{ contact.description }}</td>
            <td><button @click="removeContact(contact.id)" class="button-delete">{{ t('delete') }}</button></td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Komunikat jeśli brak kontaktów -->
    <div v-else>
      <p class="center-aligned">{{ t('no-contacts-on-list') }}</p>
    </div>
  </div>
</template>

<script>
import ContactsListService from '../services/ContactsListService'; // Serwis do API kontaktów
import Papa from 'papaparse'; // Biblioteka do parsowania CSV

export default {
  name: 'ContactList',
  data() {
    return {
      contactType: 0,           // Wybrany typ kontaktu (0=email, 1=tel)
      contactInfo: '',          // Wpisany email lub telefon
      description: '',          // Opis kontaktu
      contacts: [],             // Lista kontaktów z bazy
      csvContacts: [],          // Lista kontaktów przygotowanych z CSV do dodania
      uploadedFileName: '',     // Nazwa wgranego pliku
      currentPage: 1,           // Numer strony w paginacji
      pageSize: 5,              // Ile kontaktów na stronę
    };
  },
  computed: {
    // Lista kontaktów na bieżącą stronę
    paginatedContacts() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = this.currentPage * this.pageSize;
      return this.contacts.slice(start, end);
    },
    // Ile stron paginacji
    totalPages() {
      return Math.ceil(this.contacts.length / this.pageSize);
    }
  },
  methods: {
    // Dodaje kontakt pojedynczo przez formularz
    async addContact() {
      try {
        const response = await ContactsListService.postNewContact(
          this.contactInfo,
          this.contactType,
          this.description
        );
        // Dodanie do lokalnej listy (po udanej odpowiedzi z serwera)
        this.contacts.push({
          type: this.contactType,
          info: this.contactInfo,
          description: this.description,
          id: response.id,
        });
        // Reset pól formularza
        this.contactType = 0;
        this.contactInfo = '';
        this.description = '';
      } catch (error) {
        console.error('Error adding contact:', error);
      }
    },
    // Pobiera kontakty z serwera (wywoływane przy montowaniu)
    async fetchContacts() {
      try {
        const response = await ContactsListService.getAll();
        // Mapowanie pól API na lokalne obiekty
        this.contacts = response.map(contact => ({
          type: contact.typeOfContact,
          info: contact.contact,
          description: contact.description,
          id: contact.id,
        }));
      } catch (error) {
        console.error('Error fetching contacts:', error);
      }
    },
    // Usuwa kontakt po id
    async removeContact(id) {
      try {
        await ContactsListService.delete(id);
        // Usunięcie z lokalnej listy
        this.contacts = this.contacts.filter(contact => contact.id !== id);
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    },
    // Obsługa paginacji: następna strona
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    // Obsługa paginacji: poprzednia strona
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    // Obsługa uploadu CSV
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFileName = file.name;
        // Parsowanie CSV - oddzielone średnikami
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          delimiter: ';',
          complete: this.addContactsFromCSV,
          error: function (error) {
            console.error('Error reading CSV file:', error);
          }
        });
      }
    },
    // Callback po sparsowaniu CSV - uzupełnia tablicę csvContacts
    addContactsFromCSV(results) {
      this.csvContacts = results.data.map(contact => ({
        type: contact[0],
        info: contact[1],
        description: contact[2],
        // monitoredSince: new Date().toISOString(), // Przykład - nieużywane
        id: Date.now() + Math.random() // Unikalne tymczasowe id
      }));
    },
    // Dodaje wszystkie kontakty z CSV do bazy
    async submitCsvContacts() {
      for (let contact of this.csvContacts) {
        try {
          const response = await ContactsListService.postNewContact(
            contact.info,
            contact.type,
            contact.description
          );
          this.contacts.push({
            type: contact.type,
            info: contact.info,
            description: contact.description,
            id: response.id
          });
        } catch (error) {
          console.error('Error adding contact from CSV:', error);
        }
      }
      // Czyszczenie stanu CSV po udanym dodaniu
      this.csvContacts = [];
      this.uploadedFileName = '';
      this.$refs.fileInput.value = '';
    },
    // Usuwa nazwę pliku i listę csvContacts
    removeFile() {
      this.uploadedFileName = '';
      this.csvContacts = [];
      this.$refs.fileInput.value = '';
    },
    // Usuwa pojedynczy kontakt z csvContacts (jeszcze nie dodany do bazy)
    removeCSVContact(index) {
      this.csvContacts.splice(index, 1);
    },
  },
  // Automatycznie pobiera kontakty po załadowaniu komponentu
  mounted() {
    this.fetchContacts();
  }
};
</script>

<style scoped>
h3 {
  padding: 1rem;
  font-weight: bold;
  border-bottom: 1px solid #aeaeae;
}

.contact-form {
  margin-bottom: 20px;
  margin-top: 1.5rem;
  border-bottom: 1px solid #aeaeae;
}

.contact-form .form-group {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 10rem;
}

select {
  width: 192px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1rem;
}

.contact-form .form-group .label-contact-type {
  padding-right: 3.65rem;
}

.contact-form .form-group .label-contact-info {
  padding-right: 6rem;
}

.contact-form .form-group .label-description {
  padding-right: 3.25rem;
}

.contact-form button {
  margin-top: 1rem;
  width: 194px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1.5rem;
}

.contact-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.contact-table th,
.contact-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

.contact-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 5px 10px;
  margin: 0 5px;
}
  
p {
  font-size: large;
}

.file-upload {
  margin: 20px 0;
}

.file-upload span {
  margin-left: 10px;
}

.file-upload button {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
}

.csv-contact-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.csv-contact-table th,
.csv-contact-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

.csv-contact-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.button-delete {
  padding: 0.25rem 1rem 0.25rem 1rem;
}

.button-save {
  margin-right: 3px;
}
</style>
